import type {
  Kinerja,
  PerencanaanItemReviewDiff,
  PerencanaanDetailData,
} from 'types/kinerja'

/**
 * Compares two arrays of item IDs: the original set and the reviewed (modified) set,
 * and returns an array describing the changes for each item. Each result entry
 * contains the original and reviewed value at the same index, and a flag indicating
 * whether the review changed the item, added a new item, or left it unchanged.
 *
 * @param originalIds - Array of original item IDs before review.
 * @param reviewIds - Array of item IDs after review (may be modified).
 * @returns Array of objects for each index, showing the original, the review, and whether it was changed.
 */
export function resolvePerencanaanReviewDiff(
  originalIds: Array<number>,
  reviewIds: Array<number>
): Array<PerencanaanItemReviewDiff<number>> {
  const items: Array<PerencanaanItemReviewDiff<number>> = []

  for (let i = 0; i < reviewIds.length; i++) {
    const hasOriginal = !!originalIds[i]
    const hasReview = !!reviewIds[i]
    const hasDiff = hasReview && originalIds[i] !== reviewIds[i]
    const isAddedUponReview = hasReview && !hasOriginal
    const isChanged = hasDiff || isAddedUponReview

    items.push({
      isChanged,
      original: originalIds[i] ?? null,
      review: reviewIds[i] ?? null,
    })
  }

  return items
}

/**
 * Resolve the perencanaan detail data pegawai
 * @param data - The perencanaan data
 * @returns The resolved perencanaan detail data for pegawai
 */
export function resolvePerencanaanDetailData(
  data: Kinerja
): PerencanaanDetailData {
  if (!data) {
    return {
      tugasTambahanIds: [],
      tugasPokokIds: [],
      praktikKinerjaId: null,
      pengembanganKompetensiId: null,
      perilakuKerjaItems: [],
      reviewTugasTambahanIds: [],
      reviewTugasPokokIds: [],
      reviewPraktikKinerjaId: null,
      reviewPengembanganKompetensiId: null,
      reviewPerilakuKerjaItems: [],
    }
  }

  return {
    tugasTambahanIds: data.tugasTambahan
      // Filter out placeholder item with id=0
      ?.filter((item) => item.id !== 0)
      .map((item) => item.id),
    tugasPokokIds: data.kinerjaUtama,
    // Use praktik pembelajaran id as praktik kinerja id
    praktikKinerjaId: data.praktikPembelajaran,
    // Safely get the first item of the `pengembanganKompetensi` array
    // since it always have only 1 item
    pengembanganKompetensiId: data.pengembanganKompetensi?.[0]?.id,
    perilakuKerjaItems: data.prilakuKerja,
    reviewTugasTambahanIds: data.reviewTugasTambahan
      // Filter out placeholder item with id=0
      ?.filter((item) => item.id !== 0)
      .map((item) => item.id),
    reviewTugasPokokIds: data.reviewKinerjaUtama,
    reviewPraktikKinerjaId: data.reviewPraktikPembelajaran,
    reviewPengembanganKompetensiId: data.reviewPengembanganKompetensi?.[0]?.id,
    reviewPerilakuKerjaItems: data.reviewPrilakuKerja,
  }
}
